import type { AuthStatus } from '@framework/auth/AuthContext';
import type { UserId, UserSurveyAnswerJSON } from 'balus-common';
import { UserSurveyAnswer } from './UserSurveyAnswer';
import { RefBuilder } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { Timestamp } from '@framework/Timestamp';

type UserSurveyDisplayStatus = { status: 'Display'; id: string } | { status: 'NoDisplay' };
type UserSurveyAnswerStatus = 'AlreadyAnswered' | 'ReadyToAnswer' | 'Canceled' | 'Error';

export class UserSurveyOperation {
  private static async getAnswerStatus(userId: UserId): Promise<UserSurveyAnswer | null> {
    const snapshot = await RefBuilder.ref(RTDBPath.User.userSurveyAnswerPath(userId)).get();
    const userSurveyAnswer = snapshot.val() as UserSurveyAnswerJSON;

    return userSurveyAnswer ? UserSurveyAnswer.load(userSurveyAnswer) : null;
  }

  static async answer(userId: UserId): Promise<UserSurveyAnswerStatus> {
    const status = await this.getAnswerStatus(userId);
    if (status?.answeredAt) {
      return 'AlreadyAnswered';
    }

    try {
      const newStatus = UserSurveyAnswer.buildAnswerNew(status);
      await RefBuilder.ref().update({
        [RTDBPath.User.userSurveyAnswerPath(userId)]: newStatus.dump(),
      });
      return 'ReadyToAnswer';
    } catch (e) {
      console.error(e);
      return 'Error';
    }
  }

  static async cancel(userId: UserId) {
    const status = await this.getAnswerStatus(userId);
    if (status?.answeredAt) {
      return 'AlreadyAnswered';
    }

    try {
      const newStatus = UserSurveyAnswer.buildCancelNew(status);
      await RefBuilder.ref().update({
        [RTDBPath.User.userSurveyAnswerPath(userId)]: newStatus.dump(),
      });
      return 'Canceled';
    } catch (e) {
      console.error(e);
      return 'Error';
    }
  }

  static async getDisplayStatus(auth: AuthStatus): Promise<UserSurveyDisplayStatus> {
    if (auth.signInAt === null || auth.currentUserId === null || auth.userType === 'anonymous') {
      return { status: 'NoDisplay' };
    }

    const status = await this.getAnswerStatus(auth.currentUserId);
    if (!status) {
      // 本機能導入前から存在するユーザの場合、レコードがない
      return { status: 'NoDisplay' };
    }

    if (!status.answeredAt && !status.canceledAt) {
      // 新規作成ユーザ（回答もキャンセルもしていない）の場合、アンケートを表示する
      return { status: 'Display', id: status.surveyId };
    }

    if (status.canceledAt && status.canceledAt < auth.signInAt) {
      const canceledAt = new Timestamp(status.canceledAt);
      if (canceledAt.isBefore(Timestamp.now().addDays(-14))) {
        // 回答キャンセルがログイン前、かつ２週間より前の場合はアンケートを再表示する
        return { status: 'Display', id: status.surveyId };
      }
    }

    return { status: 'NoDisplay' };
  }
}
